import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Inbox, Plus, Search } from "lucide-react";
import { useState } from "react";
import { TemplateGridSkeleton } from "~/components/loading-skeleton";
import { TemplateCard } from "~/components/templates/template-card";
import { TemplateFilters } from "~/components/templates/template-filters";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { $getPromptTemplates } from "~/lib/queries";

export const Route = createFileRoute("/(authenticated)/dashboard/settings/templates/")({
  component: TemplatesPage,
  pendingComponent: TemplateGridSkeleton,
  wrapInSuspense: true,
});

function TemplatesPage() {
  const { data: templates } = useSuspenseQuery({
    queryKey: ["prompt-templates"],
    queryFn: () => $getPromptTemplates(),
  });

  const [search, setSearch] = useState("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);

  const filteredTemplates = templates.filter((template) => {
    const matchesSearch =
      search === "" ||
      template.name.toLowerCase().includes(search.toLowerCase()) ||
      template.description?.toLowerCase().includes(search.toLowerCase());

    const matchesTags =
      selectedTags.length === 0 ||
      selectedTags.every((tag) => template.tags?.includes(tag));

    const matchesFavorite = !showFavoritesOnly || template.isFavorite;

    return matchesSearch && matchesTags && matchesFavorite;
  });

  const allTags = Array.from(new Set(templates.flatMap((t) => t.tags ?? []))).sort();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Prompt Templates</h2>
          <p className="text-muted-foreground">Manage your reusable AI prompts</p>
        </div>
        <Button
          render={(props) => (
            <Link {...props} to="/dashboard/settings/templates/new">
              <Plus className="mr-2 h-4 w-4" />
              New Template
            </Link>
          )}
        />
      </div>

      <div className="flex flex-col gap-4 sm:flex-row">
        <div className="relative flex-1">
          <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
          <Input
            placeholder="Search templates..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <TemplateFilters
          allTags={allTags}
          selectedTags={selectedTags}
          onTagsChange={setSelectedTags}
          showFavoritesOnly={showFavoritesOnly}
          onFavoritesChange={setShowFavoritesOnly}
        />
      </div>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <div className="rounded-lg border p-4">
          <p className="text-muted-foreground text-sm">Total Templates</p>
          <p className="text-2xl font-bold">{templates.length}</p>
        </div>
        <div className="rounded-lg border p-4">
          <p className="text-muted-foreground text-sm">Favorites</p>
          <p className="text-2xl font-bold">
            {templates.filter((t) => t.isFavorite).length}
          </p>
        </div>
        <div className="rounded-lg border p-4">
          <p className="text-muted-foreground text-sm">Most Used</p>
          <p className="text-2xl font-bold">
            {templates.reduce((max, t) => Math.max(max, t.usageCount), 0)}
          </p>
        </div>
        <div className="rounded-lg border p-4">
          <p className="text-muted-foreground text-sm">Tags</p>
          <p className="text-2xl font-bold">{allTags.length}</p>
        </div>
      </div>

      {filteredTemplates.length === 0 ? (
        <div className="text-muted-foreground flex flex-col items-center py-12 text-center">
          <Inbox className="mb-4 h-12 w-12" />
          <p className="text-lg font-medium">No templates found</p>
          {templates.length === 0 ? (
            <div className="mt-4">
              <Button
                render={(props) => (
                  <Link {...props} to="/dashboard/settings/templates/new">
                    <Plus className="mr-2 h-4 w-4" />
                    Create your first template
                  </Link>
                )}
              />
            </div>
          ) : (
            <p className="text-sm">Try adjusting your search or filters</p>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredTemplates.map((template) => (
            <TemplateCard key={template.id} template={template} />
          ))}
        </div>
      )}
    </div>
  );
}
